import { useState } from 'react';

function CodeBlock({ code, language = 'json5' }: { code: string; language?: string }) {
  const [copied, setCopied] = useState(false);
  const handleCopy = () => {
    navigator.clipboard.writeText(code);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };
  return (
    <div className="relative group my-4">
      <div className="flex items-center justify-between bg-slate-800 rounded-t-lg px-4 py-2 border border-slate-700 border-b-0">
        <span className="text-xs text-slate-400 font-mono">{language}</span>
        <button onClick={handleCopy} className="flex items-center gap-1.5 text-xs text-slate-400 hover:text-cyan-400 transition-colors cursor-pointer whitespace-nowrap">
          <i className={copied ? 'ri-check-line' : 'ri-file-copy-line'}></i>
          {copied ? '已复制' : '复制'}
        </button>
      </div>
      <pre className="bg-slate-950 border border-slate-700 rounded-b-lg p-4 overflow-x-auto">
        <code className="text-sm text-slate-300 font-mono leading-relaxed">{code}</code>
      </pre>
    </div>
  );
}

function InfoBox({ type = 'info', children }: { type?: 'info' | 'warning' | 'tip'; children: React.ReactNode }) {
  const styles = {
    info: { bg: 'bg-cyan-500/5 border-cyan-500/20', icon: 'ri-information-line', iconColor: 'text-cyan-400' },
    warning: { bg: 'bg-amber-500/5 border-amber-500/20', icon: 'ri-alert-line', iconColor: 'text-amber-400' },
    tip: { bg: 'bg-emerald-500/5 border-emerald-500/20', icon: 'ri-lightbulb-line', iconColor: 'text-emerald-400' },
  };
  const s = styles[type];
  return (
    <div className={`${s.bg} border rounded-lg p-4 my-4`}>
      <div className="flex items-start gap-3">
        <div className="w-5 h-5 flex items-center justify-center flex-shrink-0 mt-0.5">
          <i className={`${s.icon} ${s.iconColor} text-lg`}></i>
        </div>
        <div className="text-sm text-slate-300 leading-relaxed">{children}</div>
      </div>
    </div>
  );
}

function SectionTitle({ id, children }: { id: string; children: React.ReactNode }) {
  return (
    <h2 id={id} className="text-xl font-bold text-white mt-10 mb-4 flex items-center gap-2 scroll-mt-24">
      <span className="w-1 h-5 bg-cyan-400 rounded-full inline-block"></span>
      {children}
    </h2>
  );
}

function SubTitle({ id, children }: { id?: string; children: React.ReactNode }) {
  return (
    <h3 id={id} className="text-base font-semibold text-slate-200 mt-6 mb-3 scroll-mt-24">{children}</h3>
  );
}

export default function SubagentsContent() {
  return (
    <div>
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-white mb-3">子智能体</h1>
        <p className="text-slate-400 text-sm leading-relaxed">
          子智能体（Subagents）允许父智能体动态派生出独立的子会话，并行执行复杂任务。
          每个子智能体拥有独立的上下文、工具权限和生命周期，适合分解大型任务或并行处理多个子任务。
        </p>
      </div>

      <SectionTitle id="sa-session-key">子智能体会话键格式</SectionTitle>
      <p className="text-slate-300 text-sm leading-relaxed mb-4">
        每个子智能体会话都有一个唯一的会话键（Session Key），格式如下：
      </p>
      <div className="bg-slate-800/40 border border-slate-700/50 rounded-lg p-4 my-4 font-mono text-sm">
        <span className="text-slate-400">格式：</span>
        <span className="text-cyan-400">subagent</span>
        <span className="text-slate-500">:</span>
        <span className="text-amber-400">&lt;parent-session-id&gt;</span>
        <span className="text-slate-500">:</span>
        <span className="text-emerald-400">&lt;subagent-name&gt;</span>
        <span className="text-slate-500">:</span>
        <span className="text-violet-400">&lt;unique-id&gt;</span>
      </div>
      <CodeBlock language="bash" code={`# 示例会话键
subagent:sess_abc123:code-reviewer:01HXYZ
subagent:sess_abc123:test-runner:02HXYZ
subagent:sess_abc123:doc-writer:03HXYZ`} />

      <SectionTitle id="sa-slash-cmd">/subagents 斜杠命令</SectionTitle>
      <p className="text-slate-300 text-sm leading-relaxed mb-4">
        使用 <code className="text-cyan-400 bg-slate-800 px-1.5 py-0.5 rounded text-xs">/subagents</code> 命令可以查看和管理当前运行中的子智能体：
      </p>
      <CodeBlock language="bash" code={`# 列出所有运行中的子智能体
/subagents

# 查看特定子智能体的状态
/subagents status code-reviewer

# 终止特定子智能体
/subagents kill code-reviewer

# 终止所有子智能体
/subagents kill-all

# 查看子智能体的输出日志
/subagents logs code-reviewer`} />

      <SectionTitle id="sa-spawn">sessions_spawn 工具参数</SectionTitle>
      <p className="text-slate-300 text-sm leading-relaxed mb-4">
        父智能体通过调用 <code className="text-cyan-400 bg-slate-800 px-1.5 py-0.5 rounded text-xs">sessions_spawn</code> 工具来创建子智能体：
      </p>
      <div className="overflow-x-auto my-4">
        <table className="w-full text-sm border-collapse">
          <thead>
            <tr className="border-b border-slate-700">
              <th className="text-left py-2.5 px-3 text-slate-400 font-medium w-1/4">参数</th>
              <th className="text-left py-2.5 px-3 text-slate-400 font-medium w-1/6">类型</th>
              <th className="text-left py-2.5 px-3 text-slate-400 font-medium">说明</th>
            </tr>
          </thead>
          <tbody>
            {[
              { key: 'name', type: 'string', desc: '子智能体名称，用于标识和引用（必填）' },
              { key: 'agent', type: 'string', desc: '使用的智能体配置名称，默认继承父智能体' },
              { key: 'task', type: 'string', desc: '子智能体的初始任务描述（必填）' },
              { key: 'tools', type: 'string[]', desc: '子智能体可用的工具列表，不能超过父智能体权限' },
              { key: 'model', type: 'string', desc: '子智能体使用的模型，默认继承父智能体' },
              { key: 'context', type: 'object', desc: '传递给子智能体的上下文数据' },
              { key: 'timeout', type: 'number', desc: '子智能体超时时间（毫秒），默认 300000（5分钟）' },
              { key: 'onComplete', type: 'string', desc: '子智能体完成后的回调指令' },
            ].map((row, i) => (
              <tr key={i} className="border-b border-slate-800/60 hover:bg-slate-800/30 transition-colors">
                <td className="py-2.5 px-3"><code className="text-cyan-400 font-mono text-xs">{row.key}</code></td>
                <td className="py-2.5 px-3"><code className="text-amber-400 font-mono text-xs">{row.type}</code></td>
                <td className="py-2.5 px-3 text-slate-300">{row.desc}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <CodeBlock language="json5" code={`// sessions_spawn 调用示例
{
  "tool": "sessions_spawn",
  "params": {
    "name": "code-reviewer",
    "task": "审查以下代码并给出改进建议：\\n\\n\${code}",
    "tools": ["group:fs", "web_search"],
    "model": "claude-3-5-sonnet-20241022",
    "context": {
      "language": "python",
      "style_guide": "PEP 8",
    },
    "timeout": 120000,
  }
}`} />

      <SectionTitle id="sa-auth">认证机制</SectionTitle>
      <p className="text-slate-300 text-sm leading-relaxed mb-4">
        子智能体的认证基于父智能体的会话令牌，遵循以下规则：
      </p>
      <ul className="space-y-2.5 text-sm text-slate-300">
        {[
          '子智能体自动继承父智能体的认证令牌，无需单独配置',
          '子智能体的工具权限不能超过父智能体的权限范围',
          '子智能体无法访问父智能体的私有记忆，但可以访问共享记忆',
          '子智能体的操作日志会关联到父会话，便于审计追踪',
          '父智能体可以随时终止子智能体，子智能体无法拒绝',
        ].map((item, i) => (
          <li key={i} className="flex items-start gap-2.5">
            <i className="ri-shield-check-line text-cyan-400 mt-0.5 flex-shrink-0"></i>
            <span>{item}</span>
          </li>
        ))}
      </ul>
      <InfoBox type="warning">
        子智能体的工具权限严格受父智能体限制。即使在 <code className="text-amber-300 bg-slate-800 px-1 py-0.5 rounded text-xs">sessions_spawn</code> 中指定了更高权限的工具，
        系统也会自动降级到父智能体的权限范围内。
      </InfoBox>

      <SectionTitle id="sa-announce">通告步骤</SectionTitle>
      <p className="text-slate-300 text-sm leading-relaxed mb-4">
        子智能体在执行过程中会通过通告（Announce）机制向父智能体汇报进度：
      </p>
      <div className="space-y-3 my-4">
        {[
          { step: '1', label: '派生（Spawn）', desc: '父智能体调用 sessions_spawn，子智能体会话创建', icon: 'ri-add-circle-line', color: 'text-cyan-400 bg-cyan-500/10' },
          { step: '2', label: '初始化', desc: '子智能体加载配置、工具和 Skills，准备执行任务', icon: 'ri-settings-3-line', color: 'text-amber-400 bg-amber-500/10' },
          { step: '3', label: '执行中', desc: '子智能体执行任务，定期通过 message 工具发送进度更新', icon: 'ri-play-circle-line', color: 'text-emerald-400 bg-emerald-500/10' },
          { step: '4', label: '完成通告', desc: '任务完成后，子智能体发送最终结果给父智能体', icon: 'ri-checkbox-circle-line', color: 'text-teal-400 bg-teal-500/10' },
          { step: '5', label: '会话清理', desc: '子智能体会话自动关闭，资源释放', icon: 'ri-delete-bin-line', color: 'text-slate-400 bg-slate-500/10' },
        ].map((item) => (
          <div key={item.step} className="flex items-start gap-3">
            <div className={`w-8 h-8 flex items-center justify-center rounded-lg flex-shrink-0 ${item.color}`}>
              <i className={`${item.icon} text-sm`}></i>
            </div>
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-0.5">
                <span className="text-xs text-slate-500 font-mono">{item.step}</span>
                <span className="text-sm font-semibold text-white">{item.label}</span>
              </div>
              <p className="text-xs text-slate-400">{item.desc}</p>
            </div>
          </div>
        ))}
      </div>

      <SectionTitle id="sa-tools-strategy">工具策略配置</SectionTitle>
      <p className="text-slate-300 text-sm leading-relaxed mb-4">
        可以在 <code className="text-cyan-400 bg-slate-800 px-1.5 py-0.5 rounded text-xs">openclaw.json</code> 中为子智能体配置默认的工具策略：
      </p>
      <CodeBlock language="json5" code={`{
  agents: [
    {
      name: "orchestrator",
      tools: ["group:runtime", "group:web", "group:sessions"],
      subagents: {
        // 子智能体默认工具集（不指定时继承父智能体）
        defaultTools: ["group:fs", "message"],
        // 子智能体工具策略：inherit（继承）/ restrict（限制）/ custom（自定义）
        toolStrategy: "restrict",
        // 子智能体黑名单工具（即使父智能体有权限也不允许子智能体使用）
        forbiddenTools: ["exec", "process"],
      }
    }
  ]
}`} />

      <SectionTitle id="sa-concurrency">并发控制</SectionTitle>
      <p className="text-slate-300 text-sm leading-relaxed mb-4">
        OpenClaw 支持多个子智能体并发运行，默认最大并发数为 <strong className="text-white">8</strong>：
      </p>
      <CodeBlock language="json5" code={`{
  agents: [
    {
      name: "orchestrator",
      subagents: {
        // 最大并发子智能体数量（默认 8）
        maxConcurrent: 8,
        // 超出并发限制时的策略：queue（排队）/ reject（拒绝）
        overflowStrategy: "queue",
        // 队列最大长度（overflowStrategy 为 queue 时生效）
        maxQueueSize: 20,
      }
    }
  ]
}`} />
      <InfoBox type="tip">
        合理设置 <code className="text-emerald-300 bg-slate-800 px-1 py-0.5 rounded text-xs">maxConcurrent</code> 可以避免资源耗尽。
        对于 API 调用密集型任务，建议根据 API 速率限制调整此值。
      </InfoBox>

      <SectionTitle id="sa-limits">限制说明</SectionTitle>
      <ul className="space-y-2.5 text-sm text-slate-300">
        {[
          { icon: 'ri-error-warning-line', color: 'text-amber-400', text: '子智能体不能再派生子智能体（不支持多层嵌套），只有顶层智能体可以创建子智能体' },
          { icon: 'ri-error-warning-line', color: 'text-amber-400', text: '子智能体的工具权限严格受父智能体限制，无法提权' },
          { icon: 'ri-error-warning-line', color: 'text-amber-400', text: '子智能体超时后会被强制终止，未完成的任务结果将丢失' },
          { icon: 'ri-error-warning-line', color: 'text-amber-400', text: '子智能体无法直接与用户交互，所有用户通信必须通过父智能体' },
          { icon: 'ri-information-line', color: 'text-cyan-400', text: '每个子智能体消耗独立的 token 配额，大量并发子智能体可能导致 API 费用增加' },
          { icon: 'ri-information-line', color: 'text-cyan-400', text: '子智能体的会话历史在任务完成后默认保留 24 小时，之后自动清理' },
        ].map((item, i) => (
          <li key={i} className="flex items-start gap-2.5">
            <i className={`${item.icon} ${item.color} mt-0.5 flex-shrink-0`}></i>
            <span>{item.text}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}
